import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import { CircularProgress, Rating } from "@mui/material";
import PropTypes from "prop-types";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import ArgonInput from "components/ArgonInput";
import api from "utils/api";
import { toast } from "react-toastify";
import StudentReviewDialog from "../components/StudentReviewDialog";
import EditIcon from "@mui/icons-material/Edit";

function StudentAuthor({ image, name }) {
  return (
    <ArgonBox display="flex" alignItems="center" px={1} py={0.5}>
      <ArgonBox mr={2}>
        <ArgonAvatar src={image} alt={name} size="md" variant="rounded" />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography variant="button" fontWeight="medium" color="dark">
          {name}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );
}

StudentAuthor.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string.isRequired,
};

function StudentReview() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  // Fetch all reviews
  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await api.get("/planwithstudentreview");
      if (response.data.success) {
        setReviews(response.data.data?.studentReviews || []);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error(error.response?.data?.message || "Failed to fetch reviews");
      setReviews([]);
    } finally {
      // Add a small delay to show loading state
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Handle edit review click
  const handleEditReviewClick = (review) => {
    setSelectedReview(review);
    setReviewDialogOpen(true);
  };

  // Handle review dialog close
  const handleReviewDialogClose = () => {
    setReviewDialogOpen(false);
    setSelectedReview(null);
  };

  // Handle update review
  const handleUpdateReview = async (reviewData) => {
    setLoading(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("studentName", reviewData.studentName);
      formData.append("rating", reviewData.rating);
      formData.append("review", reviewData.review);
      if (reviewData.photo instanceof File) {
        formData.append("photo", reviewData.photo);
      }

      const response = await api.put(
        `/planwithstudentreview/review/${selectedReview._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.success) {
        toast.success(response.data.message || "Review updated successfully!");
        fetchReviews();
        handleReviewDialogClose();
      }
    } catch (error) {
      console.error("Error updating review:", error);
      toast.error(error.response?.data?.message || "Failed to update review");
    } finally {
      // Add a small delay to show loading state
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  // Filter reviews based on search
  const filteredReviews = reviews.filter((review) =>
    review.studentName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Create table columns
  const columns = [
    { name: "sno", align: "center" },
    { name: "student", align: "left" },
    { name: "rating", align: "center" },
    { name: "review", align: "left" },
    { name: "createdAt", align: "left" },
    { name: "action", align: "center" },
  ];

  // Create table rows
  const rows = filteredReviews.map((review, index) => ({
    sno: (
      <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
        {index + 1}
      </ArgonTypography>
    ),
    student: <StudentAuthor image={review.photo} name={review.studentName} />,
    rating: <Rating value={review.rating} readOnly size="small" />,
    review: (
      <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
        {review.review?.length > 100 ? `${review.review.substring(0, 100)}...` : review.review}
      </ArgonTypography>
    ),
    createdAt: (
      <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
        {new Date(review.createdAt).toLocaleDateString()}
      </ArgonTypography>
    ),
    action: (
      <ArgonButton
        variant="text"
        color="info"
        size="medium"
        onClick={() => handleEditReviewClick(review)}
      >
        <EditIcon fontSize="large" />
      </ArgonButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={3}
              flexWrap="wrap"
              gap={2}
            >
              <ArgonTypography variant="h6">Student Reviews</ArgonTypography>

              <ArgonBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
                <ArgonInput
                  placeholder="Search by student name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: "250px" }}
                />
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching Plan ..
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <ArgonBox>
                  {filteredReviews.length === 0 ? (
                    <ArgonBox p={3} textAlign="center">
                      <ArgonTypography variant="body2" color="text">
                        No reviews found
                      </ArgonTypography>
                    </ArgonBox>
                  ) : (
                    <Table columns={columns} rows={rows} />
                  )}
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>

      {/* Review Dialog */}
      {reviewDialogOpen && selectedReview && (
        <StudentReviewDialog
          open={reviewDialogOpen}
          onClose={handleReviewDialogClose}
          review={selectedReview}
          onSave={handleUpdateReview}
          loading={loading}
        />
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default StudentReview;
